/**
 * A task's status: To do · In progress · Done · Dismissed (card ef074711,
 * owner 01.10.2026 — Linear-style, so "someone is working on this" and "we
 * decided not to" are visible, not just done / not done).
 *
 * Stored as `status` NEXT TO the old `done` flag, which stays authoritative
 * for "is it closed": filters ("show completed"), counts, the MCP board tools
 * and older app versions all read `done`. Done AND Dismissed set `done: true`
 * (closed, like Linear's completed and canceled); To do and In progress set it
 * false. If something that only knows `done` flips it (MCP, a 1.0.5 client),
 * `taskStatusOf` reconciles: a status that disagrees with `done` gives way.
 *
 * Pure — no React, no store — so the UI, the board stores and tests share it.
 */

export const TASK_STATUSES = Object.freeze(['todo', 'in_progress', 'done', 'dismissed']);

export const STATUS_LABELS = Object.freeze({
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  dismissed: 'Dismissed',
});

const isStatus = (s) => TASK_STATUSES.includes(s);

/** The status to show, reconciled with `done`. */
export function taskStatusOf(task) {
  const s = isStatus(task?.status) ? task.status : null;
  if (task?.done) return s === 'dismissed' ? 'dismissed' : 'done';
  return s === 'in_progress' ? 'in_progress' : 'todo';
}

/** The patch that sets a status — always both fields, so they never disagree. */
export function statusPatch(status) {
  const next = isStatus(status) ? status : 'todo';
  return { status: next, done: next === 'done' || next === 'dismissed' };
}

/**
 * The done date (`doneAt`, ISO) a change of `done` implies — Boards Redesign
 * EXTRAS §2.9, "done 10 Oct" on the Card-anatomy artboard.
 *
 * `{ doneAt: now }` when `patch` closes an open task, `{ doneAt: null }` when
 * it reopens a closed one (null, not undefined: the task is serialised into
 * the record and a missing key would never clear on another device), `{}`
 * otherwise — Done → Dismissed keeps the date, a patch without `done` keeps
 * it, and a patch that already carries `doneAt` (git import) is trusted.
 * Every writer (task window, card circle, list row, chat quick view, MCP,
 * in-app AI, git import) goes through this, so they cannot disagree.
 */
export function doneAtPatch(prevTask, patch, now = new Date().toISOString()) {
  if (!patch || patch.done === undefined || 'doneAt' in patch) return {};
  const was = prevTask?.done === true;
  const is = !!patch.done;
  if (was === is) return {};
  return { doneAt: is ? now : null };
}

/** `patch` plus the `doneAt` it implies against `prevTask`. */
export function withDoneAt(prevTask, patch, now) {
  const extra = doneAtPatch(prevTask, patch, now);
  return 'doneAt' in extra ? { ...patch, ...extra } : patch;
}

/** A task's done date as a Date, or null (open, or closed before 1.0.7). */
export function doneAtOf(task) {
  if (!task?.done || typeof task.doneAt !== 'string' || !task.doneAt) return null;
  const d = new Date(task.doneAt);
  return Number.isNaN(d.getTime()) ? null : d;
}
